import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { RoiCumulativePoint } from '../types/roi';
import { formatCompactCurrency, formatCurrency } from '../utils/format';

interface RoiCumulativeChartProps {
  points: RoiCumulativePoint[];
}

const CHART_INK = 'rgb(100, 116, 139)';

export function RoiCumulativeChart({ points }: RoiCumulativeChartProps) {
  return (
    <div className="h-[260px] w-full sm:h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={points}
          margin={{ top: 8, right: 8, bottom: 8, left: 0 }}
        >
          <defs>
            <linearGradient id="roi-area-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_INK} stopOpacity={0.22} />
              <stop offset="100%" stopColor={CHART_INK} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke="rgb(226, 232, 240)"
            strokeDasharray="2 4"
            vertical={false}
          />
          <XAxis
            dataKey="monthLabel"
            stroke="rgb(148, 163, 184)"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'rgb(100, 116, 139)' }}
            interval="preserveStartEnd"
            minTickGap={20}
          />
          <YAxis
            stroke="rgb(148, 163, 184)"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'rgb(100, 116, 139)' }}
            tickFormatter={(value: number) => formatCompactCurrency(value)}
            width={56}
          />
          <Tooltip
            cursor={{
              stroke: CHART_INK,
              strokeWidth: 1,
              strokeDasharray: '3 3',
            }}
            content={<CumulativeTooltip />}
          />
          <Area
            type="monotone"
            dataKey="cumulativeUsd"
            stroke={CHART_INK}
            strokeWidth={2.5}
            fill="url(#roi-area-fill)"
            activeDot={{
              r: 5,
              fill: CHART_INK,
              stroke: 'white',
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TooltipPayloadItem {
  payload: RoiCumulativePoint;
}

interface CumulativeTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

function CumulativeTooltip({ active, payload }: CumulativeTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-slate-900">{point.monthLabel}</div>
      <div className="mt-1 tabular-nums text-slate-600">
        Cumulative · {formatCurrency(point.cumulativeUsd)}
      </div>
      <div className="tabular-nums text-slate-400">
        This month · {formatCurrency(point.monthlyUsd)}
      </div>
    </div>
  );
}
